import { createSelector } from "reselect";
import _ from "underscore";

import { emptyEmployeeProfile } from "./Reducers";
import { employeeAgeMissing, employeeUnderAge, spouseAgeMissing, spouseUnderAge, childAgeMissing, childUnderAge } from "./Validations";

import type { EmployeeProfile } from "Utils/types";

export const getEmployeeProfile = (state: any) => state.employeeProfile;
export const calculateSampleQuote = (employeeProfile: EmployeeProfile) =>
  _.without(
    employeeProfile.map((employee) => employee.age || 0),
    0,
  ).length === 0;
// returns boolean
export const isSampleQuote = createSelector(getEmployeeProfile, calculateSampleQuote);

const validateEmployeeProfile = (employeeProfile) =>
  _.all(
    employeeProfile,
    (employee) =>
      (employee.age === null || employee.age >= 18) &&
      (typeof employee.spouse_age === "undefined" || employee.spouse_age === null || employee.spouse_age >= 18),
  );

const validateEmployeeProfileAgePopulated = (
  employeeProfile, // Check that all employee profiles (and added spouses/dependents) have populated ages.
) =>
  !_.some(
    employeeProfile,
    (employee) =>
      employee.age === null ||
      (typeof employee.spouse_age !== "undefined" && employee.spouse_age === null) ||
      (employee.children && employee.children.length > 0 && _.some(employee.children, (child_age) => child_age === null)),
  );

const validateEmployeeProfileExcludingAge = (employeeProfile) =>
  _.all(
    employeeProfile,
    (employee) =>
      (employee.age === null || employee.age >= 0) && (typeof employee.spouse_age === "undefined" || employee.spouse_age === null || employee.spouse_age >= 0),
  );

export const isEmployeeProfileValid = createSelector(getEmployeeProfile, validateEmployeeProfile);
export const isEmployeeProfileAgePopulated = createSelector(getEmployeeProfile, validateEmployeeProfileAgePopulated);
export const isEmployeeProfileValidExcludingAge = createSelector(getEmployeeProfile, validateEmployeeProfileExcludingAge);
export const isEmployeeProfileTouched = createSelector(getEmployeeProfile, (employeeProfile) => !_.isEqual(employeeProfile, emptyEmployeeProfile));
// This method computes its result based on the current state of the
// employeeProfile in the Redux store, therefore it is synchronous.
export const overallError = createSelector(getEmployeeProfile, (ep) => {
  let errorMessage = null;
  let eeErrorIndex = null;
  let eeDependentErrorIndex = null;
  if (ep === emptyEmployeeProfile) {
    // no op
    errorMessage = null;
  } else if (ep.some(employeeAgeMissing)) {
    errorMessage = "An employee's age is missing";
    eeErrorIndex = ep.findIndex(employeeAgeMissing);
  } else if (ep.some(employeeUnderAge)) {
    errorMessage = "Employees must be 18+ to enroll";
    eeErrorIndex = ep.findIndex(employeeUnderAge);
  } else if (ep.some(spouseAgeMissing)) {
    errorMessage = "A spouse's age is missing";
    eeErrorIndex = ep.findIndex(spouseAgeMissing);
    eeDependentErrorIndex = 0;
  } else if (ep.some(spouseUnderAge)) {
    errorMessage = "Spouses must be 18+ to enroll";
    eeErrorIndex = ep.findIndex(spouseUnderAge);
    eeDependentErrorIndex = 0;
  } else if (ep.some(childAgeMissing)) {
    errorMessage = "A child's age is missing";
    eeErrorIndex = ep.findIndex(childAgeMissing);
    const ee = ep[eeErrorIndex];
    const spouseIndex = ee.spouse_age !== null && ee.spouse_age !== undefined ? 1 : 0;
    eeDependentErrorIndex = ee.children.findIndex((child) => child === null) + spouseIndex;
  } else if (ep.some(childUnderAge)) {
    errorMessage = "A child's age must be 0+ to enroll";
    eeErrorIndex = ep.findIndex(childUnderAge);
    const ee = ep[eeErrorIndex];
    const spouseIndex = ee.spouse_age !== null && ee.spouse_age !== undefined ? 1 : 0;
    eeDependentErrorIndex = ee.children.findIndex((child) => child === null) + spouseIndex;
  }
  return { errorMessage, eeErrorIndex, eeDependentErrorIndex };
});
// This method returns the value that is stored by the Sagas in the Redux Store,
// Therefore it is asynchronous, and the value is updated after a delay in the Sagas.
export const employeeProfileOverallError = (state) => state.employeeProfileOverallError;
